import React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styles from './Patient.module.css';
import Header from './components/Header';
import PatientSummaryCard from './components/PatientSummaryCard';
import CreatePatientPopup from '../../popup/CreatePatientPopup/CreatePatientPopup';
import ModuleNav from '../appointment/components/moduleNav/ModuleNav';
import PersonalInfo from './areas/personal-info/PersonalInfo';
import AppointmentsHistory from './areas/appointments-history/AppointmentsHistory';
import { GetPatientAPI } from '../../api/API';
import { DataProviderInstance } from '../../api/DataProvider';
import Loading from '../../components/loading/Loading';
import HealthHistory from './areas/heatlh-history/HealthHistory';
import Financial from './areas/financial/Financial';
import Documents from './areas/documents/Documents';
import FiscalInvoices from './areas/fiscal/FiscalInvoices';
import PatientActivityHistory from './areas/activity-history/PatientActivityHistory';
import PatientInsurance from './areas/insurance/PatientInsurance';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import TombstoneNotice from './components/TombstoneNotice';
import Toast from '../../components/support/Toast';

// Seções cujo conteúdo é o prontuário: na ficha lápide (paciente excluído)
// viram só o aviso TombstoneNotice. Convênio entra também: o número da
// carteirinha identifica a pessoa e não se cadastra carteirinha em ficha excluída.
const TOMBSTONE_SECTIONS = ['personal', 'health', 'documents', 'insurance'];

// Seções que podem ser abertas direto pelo endereço (?section=insurance)
const LINKABLE_SECTIONS = ['insurance'];

function Patient() {

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get('section');
  const { canAccessHealthData, isOwner } = useRoleAccess();

  const [createPatientPopupVisibility, ShowCreatePatientPopup] = useState(false);
  const [currentSection, setCurrentSection] = useState('personal');
  const [accessError, setAccessError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [needsReload, setNeedsReload] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Toast "Paciente excluído." fica na página: o painel que abriu a exclusão
  // é desmontado quando a ficha vira lápide.
  const [deletedToastOpen, setDeletedToastOpen] = useState(false);
  const { GetPatients } = DataProviderInstance();

  // A seção "Saúde" pede a marcação "Profissional de saúde", não o papel
  const canAccessHealthTab = canAccessHealthData();
  // O histórico de atendimento depende da marcação "quem atende", não do papel.
  // Quem é Proprietário(a) liga a própria marcação em Usuários; os demais pedem a ela.
  const healthTabLockedReason = isOwner()
    ? 'O histórico de atendimento fica com quem atende. Para ver, marque você como quem atende em Usuários.'
    : 'O histórico de atendimento fica com quem atende. Peça a quem é proprietário(a) da conta para marcar você como quem atende.';

  // Menu lateral: o clique troca a seção (não rola a página), porque Saúde,
  // Notas fiscais, Histórico e os pacotes carregam sob demanda e a leitura do
  // prontuário é auditada (LGPD): só vai ao servidor quando a pessoa abre.
  const sections = [
    { id: 'personal', label: 'Dados pessoais', icon: <PersonOutlineOutlinedIcon />, accent: 'blue' },
    { id: 'appointments', label: 'Agendamentos', icon: <CalendarMonthOutlinedIcon />, accent: 'teal' },
    {
      id: 'health',
      label: 'Saúde',
      icon: <HealthAndSafetyOutlinedIcon />,
      accent: 'pink',
      locked: !canAccessHealthTab,
      lockedReason: healthTabLockedReason,
    },
    { id: 'financial', label: 'Financeiro', icon: <AccountBalanceWalletOutlinedIcon />, accent: 'green' },
    { id: 'insurance', label: 'Convênio', icon: <BadgeOutlinedIcon />, accent: 'teal' },
    { id: 'documents', label: 'Documentos', icon: <DescriptionOutlinedIcon />, accent: 'purple' },
    { id: 'fiscal', label: 'Notas fiscais', icon: <ReceiptOutlinedIcon />, accent: 'amber' },
    { id: 'history', label: 'Histórico', icon: <HistoryOutlinedIcon />, accent: 'blue' },
  ];

  useEffect(() => {
    setCurrentSection(LINKABLE_SECTIONS.includes(requestedSection) ? requestedSection : 'personal');
    LoadData();
  }, []);

  // Atalho de outra tela (ex.: popup de cadastro, "Gerenciar na ficha"): abre a seção
  // pedida e limpa o parâmetro, para o menu lateral voltar a mandar.
  useEffect(() => {
    if (!LINKABLE_SECTIONS.includes(requestedSection)) return;
    setCurrentSection(requestedSection);
    setSearchParams({}, { replace: true });
  }, [requestedSection]);

  useEffect(() => {
    if (needsReload) {
      LoadData();
    }
  }, [needsReload]);

  const LoadData = () => {

    setIsLoading(true);
    setAccessError(null);

    try {
      GetPatientAPI(
        id,
        (response) => {

          if (response && response.error) {
            setAccessError({ message: response.message || response.error });
            return;
          }

          if (!response || !response.personalInfo) {
            setAccessError({ message: 'Dados do paciente inválidos ou incompletos' });
            return;
          }

          if (response.personalInfo.deleted_at && patientData && !patientData.personalInfo?.deleted_at) {
            setDeletedToastOpen(true);
          }
          setPatientData(response);
          setAccessError(null);
          setNeedsReload(false);
          setIsLoading(false);
        },
        (error, statusCode) => {
          if (error && typeof error == 'object') {
            setAccessError(error);
          } else {
            setAccessError({ message: 'Erro ao carregar o paciente' });
          }
        }
      );
    } catch (e) {
      setAccessError({ message: 'Erro inesperado ao carregar o paciente' });
    }
  }

  function OnEditClickHandler() {
    ShowCreatePatientPopup(true);
  }

  function HideEditPopup() {
    ShowCreatePatientPopup(false);
  }

  function handleSectionChange(sectionId) {
    // A seção "Saúde" continua no menu com cadeado; sem permissão o clique não abre
    if (sectionId === 'health' && !canAccessHealthTab) {
      return;
    }
    setCurrentSection(sectionId);
    // A pessoa pode estar no fim de uma seção longa: a nova abre a partir do topo
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function GetSection() {
    const deletedAt = patientData.personalInfo.deleted_at;
    if (deletedAt && TOMBSTONE_SECTIONS.includes(currentSection)) {
      return <TombstoneNotice deletedAt={deletedAt} />;
    }

    switch (currentSection) {
      case 'personal':
        return <PersonalInfo data={patientData.personalInfo} onEdit={OnEditClickHandler} onChange={onPatientInfoChangeHandle} />;
      case 'appointments':
        return <AppointmentsHistory data={patientData.appointments} />;
      case 'health':
        return <HealthHistory />;
      case 'financial':
        return <Financial patientData={patientData.personalInfo} data={patientData.financial} />;
      case 'insurance':
        return <PatientInsurance patientId={id} />;
      case 'documents':
        return <Documents patientData={patientData.personalInfo} list={patientData.documents} />;
      case 'fiscal':
        return <FiscalInvoices patientData={patientData.personalInfo} />;
      case 'history':
        return <PatientActivityHistory patientId={id} />;
      default:
        return <></>;
    }
  }

  function onPatientInfoChangeHandle() {
    setNeedsReload(true);
    // A agenda, o financeiro e os relatórios resolvem o nome pelo cache de
    // pacientes do DataProvider (GetPatientById), carregado uma vez por sessão.
    // Anonimizar ou excluir muda o nome, então o cache é recarregado junto.
    GetPatients(false);
  }

  function GetContent() {

    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <section className={styles.headerCard}>
            <Header Patient={patientData.personalInfo} />
          </section>
          <section className={styles.headerCard}>
            <PatientSummaryCard
              appointments={patientData.appointments}
              financial={patientData.financial}
              onOpenSection={handleSectionChange}
            />
          </section>
        </div>

        <div className={styles.layout}>
          <ModuleNav
            sections={sections}
            title="Ficha"
            ariaLabel="Seções da ficha"
            activeId={currentSection}
            onSelect={handleSectionChange}
          />
          <div className={styles.content} data-tour="patient-health">
            {GetSection()}
          </div>
        </div>

        <CreatePatientPopup Data={patientData.personalInfo} Visibility={createPatientPopupVisibility} OnChange={onPatientInfoChangeHandle} OnHide={HideEditPopup} />
        <Toast
          open={deletedToastOpen}
          message="Paciente excluído."
          severity="success"
          onClose={() => setDeletedToastOpen(false)}
        />
      </div>
    );
  }

  const ErrorMessage = () => (
    <div className="error-container" style={{
      padding: '30px',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '60px auto',
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
    }}>
      <div style={{
        fontSize: '22px',
        fontWeight: '500',
        color: '#333333',
        marginBottom: '15px'
      }}>
        Paciente não encontrado
      </div>
    </div>
  );

  return (
    <div className='page-content'>
      {accessError ? (
        <ErrorMessage />
      ) : patientData == null && isLoading ? (
        <div className='page-content'>
          <Loading />
        </div>
      ) : patientData == null && !isLoading ? (
        <ErrorMessage />
      ) : (
        GetContent()
      )}
    </div>
  );
}

export default Patient;
